import React, { useEffect, useRef, useState } from "react";
import styles from "../styles/BusinessInfo.module.css";
import {
    LinkOutlined,
    FileTextOutlined,
    EditOutlined,
    InboxOutlined,
} from "@ant-design/icons";
import { Button, Input, Tabs, Upload, message, Collapse } from "antd";
import { useMedia } from "react-use";
import { useBusiness } from "../../_actions/finance";
import { useAtom } from "jotai";
import { orgOBAtom } from "../../onboarding/BrandOrganizationSetup";
import UploadedCard from "../UploadedCard";

const { Dragger } = Upload;

export default function BusinessInfo({
    answers,
    onAnswerChange,
    dataMe,
    onNextSlide,
    setNextButtonProps = () => {},
}) {
    const [urlInput, setUrlInput] = useState("");
    const [uploadedFile, setUploadedFile] = useState(null);

    const isMobile = useMedia("(max-width: 800px)");
    const urlInputRef = useRef(null);

    const [orgOB, setOrgOB] = useAtom(orgOBAtom);

    const { mutate: processUrl } = useBusiness(
        "business/process_url",
        (response) => {
            const apiData = response.data;
            const logos = response.logos;

            const extractNameFromUrl = (url) => {
                try {
                    const host = new URL(url).hostname.replace("www.", "");
                    return host.split(".")[0].replace(/-/g, " ").trim();
                } catch {
                    return "";
                }
            };

            const fallbackName = extractNameFromUrl(urlInput);
            const resolvedName = apiData?.name || fallbackName || "";

            onAnswerChange("name", resolvedName);

            onAnswerChange("businessDescription", apiData.description || "");

            onAnswerChange("apiData", {
                name: resolvedName,
                businessModel: apiData.business_model,
                industry: apiData.industry,
                businessUSP: apiData.USP,
                targetInterests: apiData.brand_vibes,
                targetAudience: apiData.target_audience,
                mainLogo: logos?.default_logo,
                squareLogo: logos?.square_logo,
                logoDark: logos?.dark_logo,
            });

            onAnswerChange("mainLogo", logos?.default_logo);
            onAnswerChange("squareLogo", logos?.square_logo);
            onAnswerChange("darkLogo", logos?.dark_logo);

            setUrlInput("");
            message.success("Successfully imported business information!");
            setNextButtonProps({ text: "Next", onClick: null });

            if (onNextSlide) {
                onNextSlide({
                    name: resolvedName,
                    businessModel: apiData.business_model,
                    industry: apiData.industry,
                    businessUSP: apiData.USP,
                    targetInterests: apiData.brand_vibes,
                    targetAudience: apiData.target_audience,
                    mainLogo: logos?.default_logo,
                    squareLogo: logos?.square_logo,
                    logoDark: logos?.dark_logo,
                });
            }
        },
        () => setNextButtonProps({ loading: false }),
        "",
        (error) => {
            console.error("URL import error:", error);
            message.error("Failed to import from URL. Please try again.");
            setNextButtonProps({ loading: false });
            setOrgOB({ step: "process_url" });
        }
    );

    const { mutate: processDescription } = useBusiness(
        "business/process_description",
        (response) => {
            const apiData = response.data;

            const extractedName = apiData?.name;

            onAnswerChange("name", extractedName);

            onAnswerChange("apiData", {
                name: extractedName,
                businessModel: apiData.business_model,
                industry: apiData.industry,
                businessUSP: apiData.USP,
                targetInterests: apiData.brand_vibes,
                targetAudience: apiData.target_audience,
            });

            setNextButtonProps({ text: "Next", onClick: null });
            message.success("Business description processed successfully!");

            if (onNextSlide) {
                onNextSlide({
                    name: extractedName,
                    businessModel: apiData.business_model,
                    industry: apiData.industry,
                    businessUSP: apiData.USP,
                    targetInterests: apiData.brand_vibes,
                    targetAudience: apiData.target_audience,
                });
            }
        },
        () =>
            setNextButtonProps((prev = {}) => ({
                ...prev,
                loading: false,
            })),
        "",
        () => {
            message.error("Failed to process description.");
            setNextButtonProps((prev = {}) => ({
                ...prev,
                loading: false,
            }));
            setOrgOB({ step: "process_text" });
        }
    );

    const handleFileUpload = (file) => {
        setUploadedFile(file);
        onAnswerChange("uploadedDocument", file);
        setNextButtonProps({ text: "Next" });
        message.success(`${file.name} uploaded successfully`);
        return false;
    };

    const isSoftValidUrl = (str) => {
        if (!str) return false;
        return /^((https?:\/\/)?[\w.-]+\.[a-z]{2,})(\/.*)?$/i.test(str.trim());
    };

    const modifyNextButton = () => {
        setNextButtonProps({
            text: "Import from URL",
            onClick: () => {
                if (!isSoftValidUrl(urlInput)) {
                    return message.warning("Please enter a valid URL");
                }

                setNextButtonProps({ text: "Importing...", loading: true });

                let finalUrl = urlInput.trim();
                if (!finalUrl.startsWith("http")) {
                    finalUrl = "https://" + finalUrl;
                }

                processUrl({
                    url: finalUrl,
                    selected_org: dataMe?.selected_org?.id,
                });
            },
        });

        setOrgOB({ step: "process_url" });
    };

    const modifyNextButtonForText = (text) => {
        const desc = text ?? answers.businessDescription ?? "";
        if (!desc || desc.trim().length < 20) {
            setNextButtonProps({});
            return;
        }

        setNextButtonProps({
            text: "Process Description",
            onClick: () => {
                setNextButtonProps({ text: "Processing...", loading: true });

                processDescription({
                    content: desc.trim(),
                    selected_org: dataMe?.selected_org?.id,
                });
            },
        });

        setOrgOB({ step: "process_text" });
    };

    useEffect(() => modifyNextButton(), [urlInput]);
    useEffect(() => {
        if (answers?.businessDescription)
            modifyNextButtonForText(answers.businessDescription);
    }, [answers?.businessDescription]);

    const items = [
        {
            key: "url",
            label: "Import from URL",
            icon: <LinkOutlined />,
            children: (
                <div className={styles.innerContent}>
                    <Input.TextArea
                        size="large"
                        placeholder="Enter website URL (e.g., https://example.com)"
                        value={urlInput}
                        onChange={(e) => setUrlInput(e.target.value)}
                        ref={urlInputRef}
                        className={styles.uniformInputHeight}
                    />
                </div>
            ),
        },
        {
            key: "document",
            label: "Import from Document",
            icon: <FileTextOutlined />,
            children: (
                <div className={styles.innerContent}>
                    {!uploadedFile ? (
                        <Dragger
                            accept=".pdf,.txt,.doc,.docx"
                            multiple={false}
                            beforeUpload={handleFileUpload}
                            className={styles.uniformInputHeight}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag file to upload
                            </p>
                            <p className="ant-upload-hint">
                                Supports .txt, .pdf, .doc, .docx files
                            </p>
                        </Dragger>
                    ) : (
                        <UploadedCard
                            file={uploadedFile}
                            onRemove={() => {
                                setUploadedFile(null);
                                setNextButtonProps({});
                            }}
                        />
                    )}
                </div>
            ),
        },
        {
            key: "text",
            label: "Paste Text",
            icon: <EditOutlined />,
            children: (
                <div className={styles.innerContent}>
                    <Input.TextArea
                        size="large"
                        placeholder="Enter your business description..."
                        value={answers.businessDescription || ""}
                        onChange={(e) => {
                            const value = e.target.value;
                            onAnswerChange("businessDescription", value);
                            modifyNextButtonForText(value);
                        }}
                        className={`${styles.responsiveTextarea} ${styles.uniformInputHeight}`}
                    />
                </div>
            ),
        },
    ];

    return (
        <div className={styles.slideContent}>
            <label className={styles.slideTitle}>
                Auto import business information
            </label>
            <p className={styles.slideSubtitle}>
                Import your business information from a website, text, or
                document
            </p>

            {isMobile ? (
                <Collapse
                    accordion
                    defaultActiveKey={["url"]}
                    items={items}
                    className={styles.fullWidth}
                    onChange={(key) => {
                        const active = Array.isArray(key) ? key[0] : key;

                        if (active === "url") modifyNextButton();
                        else if (active === "text")
                            modifyNextButtonForText(
                                answers.businessDescription
                            );
                        else {
                            setNextButtonProps({});
                            setOrgOB({ step: "process_text" });
                        }
                    }}
                />
            ) : (
                <Tabs
                    defaultActiveKey="url"
                    items={items}
                    type="card"
                    onTabClick={(key) => {
                        if (key === "url") modifyNextButton();
                        else if (key === "text")
                            modifyNextButtonForText(
                                answers.businessDescription
                            );
                        else {
                            setNextButtonProps({});
                            setOrgOB({ step: "process_text" });
                        }
                    }}
                />
            )}
        </div>
    );
}
